import React, { useState } from "react";
import PricingToggle from "./pricingToggle";
import { readCart, writeCart } from "../../modules/checkoutHelpers";

const featureCategories = [
  {
    name: "Core Store Credit",
    features: [
      { name: "Refunds with store credit", starter: true, growth: true, managed: true, tooltip: "Refund any order in full or in part with store credit." },
      { name: "Signup Credit", starter: true, growth: true, managed: true, tooltip: "Reward customers from day one with instant bonuses for signing up." },
      { name: "Email notifications", starter: true, growth: true, managed: true, tooltip: "Customers are notified every time their store credit is added, deducted, or earned." },
      { name: "Shopify New Customer Accounts", starter: true, growth: true, managed: true, tooltip: "Lets customers log in using a one-time 6-digit verification code sent by email." },
      { name: "Unlimited credit issued", starter: true, growth: true, managed: true, tooltip: "Never metered. Same price at $10 issued and $10,000,000 issued." },
      { name: "Unlimited team accounts", starter: true, growth: true, managed: true },
    ],
  },
  {
    name: "Cashback",
    features: [
      { name: "Unlimited cashback on unlimited orders", starter: true, growth: true, managed: true },
      { name: "Custom cashback rates", starter: true, growth: true, managed: true },
      { name: "Order-based cashback triggers", starter: true, growth: true, managed: true },
    ],
  },
  {
    name: "Loyalty & Rewards",
    features: [
      { name: "Reviews, referrals, and loyalty program", starter: false, growth: true, managed: true, tooltip: "Reward customers for reviews, referrals, and common loyalty activities." },
      { name: "Recurring Credit", starter: false, growth: true, managed: true, tooltip: "Schedule custom frequencies to keep engagement high." },
      { name: "Opt-in affiliate program", starter: false, growth: true, managed: true, tooltip: "Each participating customer has an affiliate link; a portion of each sale generated through the link goes back to the affiliate." },
    ],
  },
  {
    name: "Memberships",
    features: [
      { name: "Memberships technology", starter: false, growth: true, managed: true },
      { name: "Unlimited membership subscribers", starter: false, growth: true, managed: true },
      { name: "Attachable perks", starter: false, growth: true, managed: true, tooltip: "Customize every payout, perk, and tier to perfectly match your brand." },
      { name: "Pay-to-play or free entry tiers", starter: false, growth: true, managed: true, tooltip: "Membership tiers that customers can buy through your Shopify store." },
    ],
  },
  {
    name: "API & Integrations",
    features: [
      { name: "Full API access", starter: false, growth: true, managed: true, tooltip: "Full REST API for custom integrations." },
      { name: "White labeled and customizable", starter: false, growth: true, managed: true, tooltip: "Customizable views to perfectly match your shop's theme and branding." },
    ],
  },
  {
    name: "Support",
    features: [
      { name: "Live chat support (limited hours)", starter: true, growth: false, managed: false },
      { name: "Live chat support (24/7)", starter: false, growth: true, managed: true },
      { name: "Priority support", starter: false, growth: true, managed: true },
      { name: "Dedicated Pabloo economist", starter: false, growth: false, managed: true, tooltip: "A dedicated economist designs, runs, and optimizes your entire store credit program." },
      { name: "Custom credit strategy", starter: false, growth: false, managed: true },
    ],
  },
];

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" className="mx-auto">
      <circle cx={12} cy={12} r={10} fill="#16a34a" fillOpacity={0.12} />
      <path d="M7.5 12.5l3 3 6-6" stroke="#16a34a" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon({ dark }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" className="mx-auto">
      <path d="M17 7L7 17M7 7l10 10" stroke={dark ? "#4b5563" : "#d1d5db"} strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-block ml-1 align-middle">
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="cursor-help text-gray-400 hover:text-gray-600"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none">
          <circle cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={1.5} />
          <path d="M12 11v5M12 8v.01" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
      </span>
      {visible && (
        <span className="absolute z-20 left-1/2 -translate-x-1/2 bottom-6 w-52 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 shadow-xl pointer-events-none leading-relaxed">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

export default function FeatureComparisonTable() {
  const [frequency, setFrequency] = useState("annually");

  const handleFrequencyChange = (val) => {
    setFrequency(val);
    readCart((currentCartData) => {
      writeCart({ ...currentCartData, isMonthlyFrequency: val === "monthly" });
    });
  };

  const starterPrice = frequency === "monthly" ? "$49" : "$39";
  const growthPrice  = frequency === "monthly" ? "from $149" : "from $119";

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-8">

      {/* Heading */}
      <div className="text-center mb-10">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">Compare plans</h2>
        <p className="text-gray-500 text-base">Everything included in each plan, side by side.</p>
      </div>

      {/* Toggle */}
      <div className="flex flex-col items-center mb-8 gap-2">
        <PricingToggle frequency={frequency} onFrequencyChange={handleFrequencyChange} />
        {frequency === "annually" && (
          <p className="text-green-700 text-xs font-semibold">Save up to 20% with annual billing</p>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-5 px-6 font-medium text-gray-400 text-xs uppercase tracking-wider w-2/5">
                Features
              </th>

              {/* Starter */}
              <th className="py-5 px-5 text-center w-1/5">
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Starter</div>
                <div className="text-xl font-extrabold text-gray-900 tracking-tight">
                  {starterPrice}
                  <span className="text-xs font-normal text-gray-400"> /mo</span>
                </div>
                <a
                  href="https://app.pabloo.com/sign-up"
                  className="mt-3 inline-block w-full rounded-xl border border-gray-300 text-gray-800 text-xs font-semibold py-2 px-3 hover:bg-gray-50 transition-colors"
                >
                  Get started
                </a>
              </th>

              {/* Growth */}
              <th className="py-5 px-5 text-center w-1/5 bg-green-900">
                <div className="mb-1">
                  <span className="inline-block bg-green-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                    Most popular
                  </span>
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-green-400 mb-1">Growth</div>
                <div className="text-xl font-extrabold text-white tracking-tight">
                  {growthPrice}
                  <span className="text-xs font-normal text-green-600"> /mo</span>
                </div>
                <a
                  href="https://app.pabloo.com/sign-up"
                  className="mt-3 inline-block w-full rounded-xl bg-green-500 text-white text-xs font-semibold py-2 px-3 hover:bg-green-400 transition-colors"
                >
                  Get started
                </a>
              </th>

              {/* Managed Economy */}
              <th className="py-5 px-5 text-center w-1/5 bg-gray-900">
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Managed Economy</div>
                <div className="text-xl font-extrabold text-white tracking-tight">Custom</div>
                <a
                  href="/bookCall"
                  className="mt-3 inline-block w-full rounded-xl border border-gray-700 text-gray-300 text-xs font-semibold py-2 px-3 hover:border-gray-500 hover:text-white transition-colors"
                >
                  Talk to us
                </a>
              </th>
            </tr>
          </thead>

          <tbody>
            {featureCategories.map((category, catIdx) => (
              <React.Fragment key={catIdx}>
                <tr className="border-t border-gray-100 bg-gray-50">
                  <td colSpan={4} className="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-widest">
                    {category.name}
                  </td>
                </tr>
                {category.features.map((feature, featIdx) => (
                  <tr key={featIdx} className="border-t border-gray-100 hover:bg-gray-50/60 transition-colors">
                    <td className="py-3.5 px-6 text-gray-700 font-medium">
                      {feature.name}
                      {feature.tooltip && <InfoTooltip text={feature.tooltip} />}
                    </td>
                    <td className="py-3.5 px-5 text-center">
                      {feature.starter ? <CheckIcon /> : <XIcon />}
                    </td>
                    <td className="py-3.5 px-5 text-center bg-green-900/5">
                      {feature.growth ? <CheckIcon /> : <XIcon />}
                    </td>
                    <td className="py-3.5 px-5 text-center bg-gray-900">
                      {feature.managed ? <CheckIcon /> : <XIcon dark />}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
